import React, { useEffect, useState, useRef } from 'react';
import { Box, FormControlLabel, FormHelperText, Switch, Typography } from '@mui/material';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Handle, Position } from 'reactflow';
import { botFlowDataAtom, flowObjAtom, flowValidationAtom } from '../../../../Services/_states';
import { useRecoilValue } from 'recoil';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import { FlowBuilderActions } from '../../../../Services/_actions';
import CloseIcon from '@mui/icons-material/Close';
import LoadingWrapper from '../../../Shared/LoadingWrapper';
import RenderTextArea from './RenderTextArea.tsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import { getAllParentNodeResults } from '../../helper.tsx';

interface SuggetionObj {
  label: string;
  value: string;
  paramType?: string;
}

const ButtonNode = ({ data }: any) => {
  const actions = FlowBuilderActions();
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const botFlowData: any = useRecoilValue(botFlowDataAtom);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackBar } = useSnackBar();
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    const match = textBeforeCursor.match(/{{@\w*$/);
    if (match) {
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    const matches = value.match(/{{(.*?)}}/g);
    const labelsInBody = matches ? matches.map((match) => match.replace(/{{|}}/g, '')) : [];

    const categorizedParams: any = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggetionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === 'global') {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === 'result') {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === 'webform') {
          categorizedParams.webform.push(result.value);
        }
      }
    });

    data.onChange(data.id, { body: value, params: categorizedParams });
  };

  const handleSuggestionClick = (suggestion: SuggetionObj) => {
    if (cursorPosition !== null) {
      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);
      const updatedText =
        textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) + textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      const updatedParams = {
        global: data.params?.global || [],
        result: data.params?.result || [],
        webform: data.params?.webform || [],
      };

      if (suggestion.paramType === 'global') {
        updatedParams.global = [...updatedParams.global, suggestion.value];
      } else if (suggestion.paramType === 'result') {
        updatedParams.result = [...updatedParams.result, suggestion.value];
      } else if (suggestion.paramType === 'webform') {
        updatedParams.webform = [...updatedParams.webform, suggestion.value];
      }

      data.onChange(data.id, { body: updatedText, params: updatedParams });
    }
  };

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const ext = fileName.split('.').pop();

    // Check blocked extensions
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
      e.target.value = '';
      return;
    }

    // Infer type group (image, video, audio, document)
    const typeGroup = inferDocType(file.type);
    if (!typeGroup) {
      showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
      e.target.value = '';
      return;
    }

    // Check file size
    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(
        `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
        'error'
      );
      e.target.value = '';
      return;
    }

    // Upload file
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('bot_number', botFlowData?.phone_number_id || '');
      formData.append('uploaded_file', file);
      formData.append('filename', file.name);

      const resp = await actions.uploadMediaToWhatsapp(formData);
      if (resp?.status === 200) {
        const { messaging_product, ...updatedData } = resp?.data;
        const updatedMedia = { ...updatedData, filename: file.name };
        data.onChange(data.id, { media: updatedMedia });
        showSnackBar(`${type} uploaded successfully!`, 'success');
      } else {
        showSnackBar(resp?.response?.data?.detail || 'Error uploading file', 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showSnackBar('Error uploading file', 'error');
    } finally {
      setIsLoading(false);
      e.target.value = ''; // Clear input after processing
    }
  };

  // const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
  //   const file = e.target.files?.[0];

  //   if (file) {
  //      const fileName = file.name.toLowerCase();
  //         const ext = fileName.split('.').pop();

  //         if (BLOCKED_EXTENSIONS.has(ext)) {
  //           showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
  //           event.target.value = '';
  //           return;
  //         }

  //         const typeGroup = inferDocType(file.type);
  //         if (!typeGroup) {
  //           showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
  //           event.target.value = '';
  //           return;
  //         }

  //         const limit = WHATSAPP_LIMITS[typeGroup];
  //         if (file.size > limit) {
  //           showSnackBar(
  //             `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
  //             'error'
  //           );
  //           event.target.value = '';
  //           return;
  //         }
  //     setIsLoading(true);
  //     const filename = file.name;
  //     const formData = new FormData();
  //     formData.append('bot_number', botFlowData?.phone_number_id || '');
  //     formData.append('uploaded_file', file);
  //     formData.append('filename', filename);

  //     console.log(file.name);
  //     const resp = await actions.uploadMediaToWhatsapp(formData);
  //     if (resp?.status === 200) {
  //       const { messaging_product, ...updatedData } = resp?.data;
  //       const updatedMedia = {
  //         ...updatedData,
  //         filename: filename,
  //       };
  //       data.onChange(data.id, { media: updatedMedia });
  //       showSnackBar(`${type} uploaded successfully!`, 'success');
  //     } else {
  //       showSnackBar(resp?.response?.data?.detail || 'Error', 'error');
  //     }
  //     setIsLoading(false);
  //   }
  // };

  useEffect(() => {
    if (!data.buttons) {
      data.onChange(data.id, { buttons: [''] });
    }
  }, [data]);

  try {
    return (
      <div>
        <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={data.ai_mode_enabled ?? false}
                onChange={(e) => {
                  const enabled = e.target.checked;

                  if (!enabled) {
                    data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                  } else {
                    data.onChange(data.id, { ai_mode_enabled: true });
                  }
                }}
                color="primary"
              />
            }
            label="AI"
            labelPlacement="end"
          />
          {data.ai_mode_enabled && (
            <SearchableDropdown
              options={parentResults.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
              multiSelect={true}
              onChange={(selectedOptions) => {
                const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
                data.onChange(data.id, { ai_query_parameters: selectedValues });
              }}
              value={parentResults
                .map((item) => ({ label: item.label, value: item.value }))
                .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
            />
          )}
        </Box>
        <div style={{ marginBottom: '5px', position: 'relative' }}>
          <SearchableDropdown
            label="Select Header Type"
            value={data?.headerType}
            options={[
              { label: 'Text', value: 'text' },
              { label: 'Image', value: 'image' },
              { label: 'Document', value: 'document' },
              { label: 'Video', value: 'video' },
            ]}
            onChange={(value) => {
              data.onChange(data.id, { headerType: value || null });
              data.onChange(data.id, { media: '' });
            }}
          />
          {data?.headerType?.value === 'text' && (
            <>
              <div style={{ position: 'relative', paddingTop: '5px' }}>
                <input
                  type="text"
                  value={data?.header || ''}
                  onChange={(e) => {
                    const newHeader = e.target.value.slice(0, 60);
                    data.onChange(data.id, { header: newHeader });
                  }}
                  placeholder="Enter Header Text"
                  style={{
                    width: '100%',
                    padding: '5px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    background: '#f5f5f5',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: data.header?.length === 60 ? 'red' : '#888',
                  }}
                >
                  {`${(data?.header || '').length}/60`}
                </span>
              </div>
              {flowValidationObj[data?.id]?.header && (
                <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                  {flowValidationObj[data?.id]?.header}
                </Typography>
              )}
            </>
          )}
          {['image', 'video', 'document'].includes(data?.headerType?.value) && (
            <FormControlLabel
              control={
                <Switch
                  checked={data.headerTypeIsUrl || false}
                  onChange={() =>
                    data.onChange(data.id, {
                      headerTypeIsUrl: !data.headerTypeIsUrl,
                      media: {},
                    })
                  }
                />
              }
              label={`${data?.headerType?.value?.charAt(0)?.toUpperCase() + data?.headerType?.value.slice(1)} url`}
              labelPlacement="start"
            />
          )}

          {data?.headerType?.value === 'image' && (
            <div>
              {data.headerTypeIsUrl ? (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Paste image url"
                    value={data?.media?.s3_object?.url || ''}
                    onChange={(e) => {
                      const url = e.target.value;
                      const fileName = url?.split('/')?.pop() || '';
                      data.onChange(data.id, {
                        media: {
                          ...(data.media || {}),
                          url: url,
                          filename: fileName,
                          s3_object: {
                            ...(data.media?.s3_object || {}),
                            url: url,
                            filename: fileName,
                          },
                        },
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={async () => {
                              try {
                                const text = await navigator.clipboard.readText();
                                const fileName = text?.split('/')?.pop() || '';
                                data.onChange(data.id, {
                                  media: {
                                    ...(data.media || {}),
                                    url: text,
                                    filename: fileName,
                                    s3_object: {
                                      ...(data.media?.s3_object || {}),
                                      url: text,
                                      filename: fileName,
                                    },
                                  },
                                });
                              } catch (err) {
                                console.error('Failed to read clipboard:', err);
                              }
                            }}
                            edge="end"
                            aria-label="paste"
                          >
                            <ContentPasteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Paste image url"
                  />
                  {data?.media?.s3_object?.url && (
                    <div
                      style={{
                        position: 'relative', // Ensures the CloseIcon is positioned relative to this container
                        width: '100%',
                        borderRadius: '4px',
                        marginTop: '23px',
                      }}
                    >
                      <CloseIcon
                        style={{
                          position: 'absolute',
                          top: '-19px',
                          right: '-3px',
                          color: 'red',
                          cursor: 'pointer',
                          fontSize: '18px',
                        }}
                        onClick={() =>
                          data.onChange(data.id, {
                            media: {},
                          })
                        }
                      />
                      <img
                        src={data.media.s3_object.url}
                        alt="Preview"
                        style={{ width: '100%', borderRadius: '4px', marginTop: '5px' }}
                      />
                    </div>
                  )}
                </>
              ) : data?.media?.s3_object?.url ? (
                <div style={{ position: 'relative', marginTop: '15px' }}>
                  <CloseIcon
                    style={{
                      position: 'absolute',
                      top: '-42px',
                      right: '-2px',
                      color: 'red',
                      cursor: 'pointer',
                      fontSize: '18px',
                    }}
                    onClick={() =>
                      data.onChange(data.id, {
                        media: {},
                      })
                    }
                  />
                  <img
                    src={data.media.s3_object.url}
                    alt="Uploaded"
                    style={{ width: '100%', borderRadius: '4px' }}
                  />
                </div>
              ) : (
                <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                  {isLoading ? (
                    <LoadingWrapper />
                  ) : (
                    <>
                      <label>Upload Image:</label>
                      {/* <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'image')}
                        style={{ width: '100%', marginBottom: '10px' }}
                      /> */}
                      <FileInput
                        onFileSelect={(e) => handleFileUpload(e, 'image')}
                        accept="image/*"
                      />
                      <FormHelperText sx={{ mt: 0 }}>
                        Allowed: JPG, PNG, GIF • Max 10 MB
                      </FormHelperText>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {data?.headerType?.value === 'video' && (
            <div>
              {data.headerTypeIsUrl ? (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Paste image url"
                    value={data?.media?.s3_object?.url || ''}
                    onChange={(e) => {
                      const url = e.target.value;
                      const fileName = url?.split('/')?.pop() || '';
                      data.onChange(data.id, {
                        media: {
                          ...(data.media || {}),
                          url: url,
                          filename: fileName,
                          s3_object: {
                            ...(data.media?.s3_object || {}),
                            url: url,
                            filename: fileName,
                          },
                        },
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={async () => {
                              try {
                                const text = await navigator.clipboard.readText();
                                const fileName = text?.split('/')?.pop() || '';
                                data.onChange(data.id, {
                                  media: {
                                    ...(data.media || {}),
                                    url: text,
                                    filename: fileName,
                                    s3_object: {
                                      ...(data.media?.s3_object || {}),
                                      url: text,
                                      filename: fileName,
                                    },
                                  },
                                });
                              } catch (err) {
                                console.error('Failed to read clipboard:', err);
                              }
                            }}
                            edge="end"
                            aria-label="paste"
                          >
                            <ContentPasteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Paste video url"
                  />
                  {data?.media?.s3_object?.url && (
                    <div style={{ position: 'relative', marginTop: '30px' }}>
                      <CloseIcon
                        style={{
                          position: 'absolute',
                          top: '-25px',
                          right: '-3px',
                          color: 'red',
                          cursor: 'pointer',
                          fontSize: '18px',
                        }}
                        onClick={() =>
                          data.onChange(data.id, {
                            media: {},
                          })
                        }
                      />
                      <video
                        controls
                        src={data?.media?.s3_object?.url}
                        style={{ width: '100%', borderRadius: '4px' }}
                      />
                    </div>
                  )}
                </>
              ) : data?.media?.s3_object?.url ? (
                <div style={{ position: 'relative', marginTop: '15px' }}>
                  <CloseIcon
                    style={{
                      position: 'absolute',
                      top: '-42px',
                      right: '-4px',
                      color: 'red',
                      cursor: 'pointer',
                      fontSize: '18px',
                    }}
                    onClick={() =>
                      data.onChange(data.id, {
                        media: {},
                      })
                    }
                  />
                  <video
                    controls
                    src={data.media.s3_object.url}
                    style={{ width: '100%', borderRadius: '4px' }}
                  />
                </div>
              ) : (
                <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                  {isLoading ? (
                    <LoadingWrapper />
                  ) : (
                    <>
                      <label>Upload Video:</label>
                      {/* <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleFileUpload(e, 'video')}
                        style={{ width: '100%', marginBottom: '10px' }}
                      /> */}
                      <FileInput
                        onFileSelect={(e) => handleFileUpload(e, 'video')}
                        accept="video/*"
                      />
                      <FormHelperText sx={{ mt: 0 }}>Allowed: MP4, WebM • Max 16 MB</FormHelperText>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {data?.headerType?.value === 'document' && (
            <div>
              {data.headerTypeIsUrl ? (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Paste image url"
                    value={data?.media?.s3_object?.url || ''}
                    onChange={(e) => {
                      const url = e.target.value;
                      const fileName = url?.split('/')?.pop() || '';
                      data.onChange(data.id, {
                        media: {
                          ...(data.media || {}),
                          url: url,
                          filename: fileName,
                          s3_object: {
                            ...(data.media?.s3_object || {}),
                            url: url,
                            filename: fileName,
                          },
                        },
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={async () => {
                              try {
                                const text = await navigator.clipboard.readText();
                                const fileName = text?.split('/')?.pop() || '';
                                data.onChange(data.id, {
                                  media: {
                                    ...(data.media || {}),
                                    url: text,
                                    filename: fileName,
                                    s3_object: {
                                      ...(data.media?.s3_object || {}),
                                      url: text,
                                      filename: fileName,
                                    },
                                  },
                                });
                              } catch (err) {
                                console.error('Failed to read clipboard:', err);
                              }
                            }}
                            edge="end"
                            aria-label="paste"
                          >
                            <ContentPasteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Paste document url"
                  />
                  {data?.media?.s3_object?.url && (
                    <div style={{ marginTop: '15px', position: 'relative' }}>
                      <CloseIcon
                        style={{
                          position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          color: 'red',
                          cursor: 'pointer',
                          fontSize: '18px',
                        }}
                        onClick={() =>
                          data.onChange(data.id, {
                            media: {},
                          })
                        }
                      />
                      <a
                        href={data?.media?.s3_object?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block', color: '#007bff', textDecoration: 'underline' }}
                      >
                        View Document
                      </a>
                    </div>
                  )}
                </>
              ) : data?.media?.s3_object?.url ? (
                <div style={{ position: 'relative', marginTop: '15px' }}>
                  <CloseIcon
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      color: 'red',
                      cursor: 'pointer',
                      fontSize: '18px',
                    }}
                    onClick={() =>
                      data.onChange(data.id, {
                        media: { ...data.media, s3_object: { url: '' } },
                      })
                    }
                  />
                  <a
                    href={data.media.s3_object.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'block', color: '#007bff', textDecoration: 'underline' }}
                  >
                    View Uploaded Document
                  </a>
                </div>
              ) : (
                <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                  {isLoading ? (
                    <LoadingWrapper />
                  ) : (
                    <>
                      <label>Upload Document:</label>
                      {/* <input
                        type="file"
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => handleFileUpload(e, 'document')}
                        style={{ width: '100%', marginBottom: '10px' }}
                      /> */}
                      <FileInput
                        onFileSelect={(e) => handleFileUpload(e, 'document')}
                        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      />

                      <FormHelperText sx={{ mt: 0 }}>
                        Allowed: PDF, DOC, DOCX • Max 100 MB
                      </FormHelperText>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {flowValidationObj[data?.id]?.url && (
            <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
              {flowValidationObj[data?.id]?.url}
            </Typography>
          )}

          {/* <label>Body:</label>
          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              value={bodyText}
              onChange={handleBodyChange}
              placeholder="Enter text"
              maxLength={4096}
              style={{
                width: '100%',
                height: 'auto',
                padding: '5px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#f5f5f5',
                overflow: 'hidden',
                resize: 'none',
              }}
            />
            <span
              style={{
                position: 'absolute',
                right: '10px',
                bottom: '10px',
                fontSize: '12px',
                color: bodyText?.length === 4096 ? 'red' : '#888',
              }}
            >
              {`${bodyText.length}/4096`}
            </span>
          </div> */}
          <div style={{ paddingTop: '8px' }}>
            <RenderTextArea data={data} />
          </div>
          {flowValidationObj[data?.id]?.body && (
            <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
              {flowValidationObj[data?.id]?.body}
            </Typography>
          )}

          {/* {showSuggestions && data?.parentResults?.length > 0 && (
            <div
              style={{
                position: 'absolute',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: 4,
                zIndex: 1000,
                width: '100%',
              }}
            >
              {data?.parentResults?.map((suggestion: SuggetionObj, index: any) => (
                <div
                  key={`${data?.id}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  style={{
                    padding: '5px 10px',
                    cursor: 'pointer',
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                  }}
                >
                  {suggestion?.label}
                </div>
              ))}
            </div>
          )} */}
          <label>Footer:</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={data?.footer || ''}
              onChange={(e) => {
                const newFooter = e?.target?.value.slice(0, 60);
                data.onChange(data?.id, { footer: newFooter });
              }}
              placeholder="Enter Footer Text"
              style={{
                width: '100%',
                padding: '5px',
                paddingRight: '50px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#f5f5f5',
              }}
            />
            <span
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: data.footer?.length === 60 ? 'red' : '#888',
              }}
            >
              {`${(data.footer || '').length}/60`}
            </span>
          </div>
          {flowValidationObj[data?.id]?.footer && (
            <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
              {flowValidationObj[data?.id]?.footer}
            </Typography>
          )}
          <label>Buttons (Max 3):</label>
          <div>
            {(data?.buttons || [])?.map((buttonLabel: string, index: any) => (
              <>
                <div
                  key={`${data?.id}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    position: 'relative',
                  }}
                >
                  <input
                    type="text"
                    value={buttonLabel || ''}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 20);
                      const updatedButtonLabels = [...(data?.buttons || [])];
                      updatedButtonLabels[index] = newValue;
                      data.onChange(data.id, { buttons: updatedButtonLabels });
                    }}
                    placeholder={`Button ${index + 1}`}
                    style={{
                      flex: 1,
                      padding: '5px',
                      paddingRight: '50px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      background: '#f5f5f5',
                      height: '40px',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '24px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      color: buttonLabel?.length === 20 ? 'red' : '#888',
                    }}
                  >
                    {`${(buttonLabel || '')?.length}/20`}
                  </span>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`${data?.id}-button-${index}`}
                    style={{
                      background: '#555',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      position: 'absolute',
                      right: '-20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                    onClick={() => data.onOutputClick?.(data.id)}
                  />
                  {(data?.buttons || [])?.length > 1 && (
                    <span
                      onClick={() => {
                        const updatedButtonLabels = (data?.buttons || [])?.filter(
                          (_: string, i: number) => i !== index
                        );
                        data.onChange(data.id, { buttons: updatedButtonLabels });
                      }}
                      style={{
                        color: 'red',
                        cursor: 'pointer',
                        marginLeft: '10px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                      }}
                    >
                      &times;
                    </span>
                  )}
                </div>
                {(data?.buttons || []).length > 0 &&
                  flowValidationObj[data?.id]?.connectionError &&
                  flowValidationObj[data?.id]?.connectionError[index] && (
                    <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                      {flowValidationObj[data?.id]?.connectionError[index]}
                    </Typography>
                  )}
              </>
            ))}
          </div>
          {flowValidationObj[data?.id]?.errorMsg && (
            <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
              {flowValidationObj[data?.id]?.errorMsg}
            </Typography>
          )}
          {(data.buttons || []).length < 3 && (
            <Typography
              variant="body2"
              style={{
                marginTop: '10px',
                cursor: 'pointer',
                color: '#007bff',
                textDecoration: 'underline',
                fontSize: '14px',
              }}
              onClick={() => {
                const updatedButtonLabels = [...(data.buttons || []), ''];
                data.onChange(data.id, { buttons: updatedButtonLabels });
              }}
            >
              + Add Button
            </Typography>
          )}
        </div>
      </div>
    );
  } catch (error) {
    console.log(error, 'error');
  }
};

export default ButtonNode;
